import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  MutableRefObject,
  ReactNode,
} from "react";

// UX
import CardTriggerDischargeInterfaceUX from "./CardTriggerDischargeInterfaceUX";

// Common
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Utils
import { Checkbox, Icon } from "semantic-ui-react";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  MasterOptionsType,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
} from "./sequence/TriggerDischargeInterface";

// Serializer
import DisChargeInterfaceSerializer from "issara-sdk/types/DisChargeInterfaceSerializer_apps_INF";

// Type
type CardTriggerDischargeProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  TriggerDischargeInterfaceSequence: State["TriggerDischargeInterfaceSequence"];
  masterOptions: MasterOptionsType;
  languageUX: any;
} & PickedProps;

const CardTriggerDischargeInterface = (props: CardTriggerDischargeProps) => {
  const intl = useIntl();
  const hnRef = useRef(null) as MutableRefObject<any>;

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedDischarge, setSelectedDischarge] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "TriggerDischargeInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data}</div>;
  };

  const columnTableIconCheck = (data: boolean, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "center" }}>
        {data === true ? (
          <Icon name="check circle" color="green" size="big" />
        ) : (
          <Icon name="times circle" color="red" size="big" />
        )}
      </div>
    );
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList || []).map(
        (item: DisChargeInterfaceSerializer) => item.id
      );

      setSelectedDischarge(newData);
    } else {
      setSelectedDischarge([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "TriggerDischargeInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedDischarge([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "TriggerDischargeInterface",
      action: "SAVE",
      selectedItems: selectedDischarge,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedDischarge([]);
  };

  const handleClear = () => {
    hnRef.current?.clear();

    props.runSequence({
      sequence: "TriggerDischargeInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedDischarge([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`TriggerDischargeInterfaceSequence.searchDischarge.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`TriggerDischargeInterfaceSequence.searchDischarge.createdDate`, date);
  };

  // Callback
  const handleSelectedDoctor = useCallback(
    (value: any, key: any) => {
      handleChangeData(null, { name: "doctorId", value: value || null });
    },
    [props.searchedItemListWithKey]
  );

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`TriggerDischargeInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "TriggerDischargeInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedDischarge([]);
    props.setProp(`TriggerDischargeInterfaceSequence.selectedItems`, []);
  };

  // const handleDisabledTrigger = useMemo(() => {
  //   if (selectedDischarge.length === 0) return true;

  //   return !selectedDischarge.every((id) => {
  //     const row = props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList?.find(
  //       (item: DisChargeInterfaceSerializer) => item.id === id
  //     );

  //     return row?.valid === false;
  //   });
  // }, [selectedDischarge, props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList]);

  // const handleDisabledSent = useMemo(() => {
  //   if (selectedDischarge.length === 0) return true;

  //   return !selectedDischarge.every((id) => {
  //     const row = props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList?.find(
  //       (item: DisChargeInterfaceSerializer) => item.id
  //     );

  //     return row?.valid === true && row?.sent === false;
  //   });
  // }, [selectedDischarge, props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList]);

  const handleDisabledAddNEdit = useMemo(() => {
    if (selectedDischarge.length === 0) return true;
  }, [selectedDischarge]);

  // useMemo
  const dischargeInterfaceList = useMemo(() => {
    return (props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList || []).map(
      (item: DisChargeInterfaceSerializer) => {
        let createdName = (props.TriggerDischargeInterfaceSequence?.userOptions || []).find(
          (data: DisChargeInterfaceSerializer) => data?.id === item.created_by
        );

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedDischarge.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedDischarge([...selectedDischarge, item.id]);
                } else {
                  let newData = selectedDischarge.filter((key: any) => key !== item.id);
                  setSelectedDischarge(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof DisChargeInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "created_at") {
                data[key] = columnTableCenter(dateToStringBE(item[key]));
              } else if (key === "created_by") {
                data[key] = columnTableCenter(
                  (createdName?.full_name_code || "").replace(/\s*\(.*?\)/g, "")
                );
              } else if (["valid", "sent"].includes(key)) {
                data[key] = columnTableIconCheck(!!item[key]);
              } else if (key === "error_message") {
                data[key] = item[key];
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
        };
      }
    );
  }, [
    selectedDischarge,
    props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList,
    props.TriggerDischargeInterfaceSequence?.userOptions,
    tableHeaders,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardTriggerDischargeInterfaceUX
        onChangeDate={handleChangeDate}
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchDischarge={props.TriggerDischargeInterfaceSequence?.searchDischarge}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedDischarge.length ===
                    (props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList || [])
                      .length &&
                  (props.TriggerDischargeInterfaceSequence?.dischargeInterfaceList || []).length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        dischargeInterfaceList={dischargeInterfaceList}
        paginatedItems={props.TriggerDischargeInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        doctorSearchBox={
          <SearchBoxDropdown
            onEvent={props.onEvent}
            // config
            type="Doctor"
            id="CardTriggerDischargeInterface"
            style={{ width: "100%" }}
            fluid={true}
            useWithKey={true}
            icon="search"
            limit={20}
            inline={true}
            // Select
            searchedItemListWithKey={props.searchedItemListWithKey}
            selectedItem={props.TriggerDischargeInterfaceSequence?.searchDischarge?.doctorId || null}
            setSelectedItem={handleSelectedDoctor}
          />
        }
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        addButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("ADD")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_ADD`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_ADD`]}
            // config
            color="blue"
            title="Add Record"
            name="ADD"
            size="medium"
          />
        }
        editButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("EDIT")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_EDIT`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_EDIT`]}
            // config
            color="green"
            title="Edit Date"
            name="EDIT"
            size="medium"
          />
        }
      />
    </>
  );
};

CardTriggerDischargeInterface.displayName = "CardTriggerDischargeInterface";

export default React.memo(CardTriggerDischargeInterface);

const columnWidths: WidthsType = {
  id: 70,
  valid: 70,
  error_message: 150,
  created_by: 120,
  created_at: 150,
  sent: 70,
  file_name: 160,
  version: 80,
  interface_date: 130,
  encounter_type: 130,
  physicians: 120,
};

const tableHeaders = GenerateTableHeader(columnWidths);
